import { useEffect, useState, type FormEvent } from 'react';
import { Mail, UserPlus } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { FormErrorAlert } from '@/features/auth/FormErrorAlert';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import {
  EMPTY_FORM_ERRORS,
  fieldError,
  formErrorsFromApiError,
  formErrorsFromFields,
  type FormErrors,
} from '@/lib/form-errors';
import { roleLabel } from '@/theme';
import type { Role } from '@/types/domain';
import { validateMemberEmail } from './settings-validation';
import { useAddMember } from './use-members';

/**
 * Agregar un miembro al negocio (`POST /api/tenants/:tenantId/users`, solo ADMIN).
 *
 * Detalle importante del contrato: este endpoint NO crea usuarios, solo la membresía. El
 * email tiene que corresponder a una cuenta ya registrada; si no, el backend responde 404 con
 * el mensaje "No existe un usuario con el email … Primero debe registrarse" (y si ya es
 * miembro, 409). Los dos mensajes se muestran tal cual en el aviso del formulario.
 */

const MEMBER_FIELDS = ['email', 'role'] as const;

export function MemberDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { tenantId } = useActiveTenantAccess();
  const addMember = useAddMember(tenantId);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('AGENTE_HUMANO');
  const [errors, setErrors] = useState<FormErrors>(EMPTY_FORM_ERRORS);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    setEmail('');
    setRole('AGENTE_HUMANO');
    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);
  }, [open]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const emailError = validateMemberEmail(email);
    if (emailError) {
      setErrors(formErrorsFromFields({ email: emailError }));
      setSubmitError(null);
      return;
    }

    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);

    try {
      // El backend normaliza el email (minúsculas + trim); se manda ya normalizado para que el
      // mensaje de error, si lo hay, coincida con lo que el usuario escribió.
      await addMember.mutateAsync({ email: email.trim().toLowerCase(), role });
      onClose();
    } catch (error) {
      setErrors(formErrorsFromApiError(error, MEMBER_FIELDS));
      setSubmitError(getErrorMessage(error));
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Agregar miembro"
      description="Solo canjea la membresía: la persona ya debe tener su cuenta creada en el panel."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={addMember.isPending}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="member-form"
            loading={addMember.isPending}
            leftIcon={<UserPlus size={16} />}
          >
            Agregar
          </Button>
        </>
      }
    >
      <form id="member-form" onSubmit={handleSubmit} noValidate className="space-y-5">
        <FormErrorAlert message={submitError} errors={errors} />

        <Field
          label="Email"
          htmlFor="memberEmail"
          required
          hint="El del usuario ya registrado."
          error={fieldError(errors, 'email')}
        >
          <Input
            id="memberEmail"
            name="email"
            type="email"
            autoComplete="off"
            placeholder="persona@ejemplo.com"
            leftIcon={<Mail size={16} />}
            autoFocus
            value={email}
            invalid={Boolean(fieldError(errors, 'email'))}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Field
          label="Rol"
          htmlFor="memberRole"
          error={fieldError(errors, 'role')}
          hint="El administrador gestiona ajustes, integraciones, pagos y equipo; el agente humano atiende conversaciones."
        >
          <Select
            id="memberRole"
            name="role"
            value={role}
            onChange={(event) => setRole(event.target.value as Role)}
          >
            <option value="AGENTE_HUMANO">{roleLabel.AGENTE_HUMANO}</option>
            <option value="ADMIN">{roleLabel.ADMIN}</option>
          </Select>
        </Field>

        <Alert tone="info">
          Si todavía no tiene cuenta, pídele que se registre en <strong>/register</strong> y vuelve
          a agregarlo con ese mismo email.
        </Alert>
      </form>
    </Modal>
  );
}
